// AF-3.16 — MetricCard: renders one metric (primary or guardrail) using
// the backend's own baseline/current/delta/direction/status fields.
//
// CRITICAL: if baseline_status !== "PROVIDED", this renders literally
// "Baseline unavailable" -- it never computes or displays a percentage
// from an absent/zero baseline (spec Section 5).

import { StatusBadge } from "./Badges";
import { cn } from "@/lib/utils";

function formatDelta(deltaPct) {
  if (deltaPct === null || deltaPct === undefined) return null;
  const sign = deltaPct > 0 ? "+" : "";
  return `${sign}${deltaPct.toFixed(2)}%`;
}

export function MetricCard({
  label,
  baseline, // { baseline_status, baseline_value }
  current,  // { current_value, direction, delta, delta_pct }
  status,   // e.g. outcome_status, or "BREACHED"/"CLEAR" for a guardrail
  isGuardrail = false,
  className,
}) {
  const baselineProvided = baseline?.baseline_status === "PROVIDED";
  const hasCurrent = current?.current_value !== null && current?.current_value !== undefined;

  return (
    <div data-testid={`metric-card-${label?.toLowerCase()}`} className={cn("rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]", className)}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)]">{label}</p>
        {isGuardrail && <span className="text-[9px] font-mono uppercase text-[var(--text-tertiary)] border border-[var(--border)] rounded px-1.5 py-0.5">Guardrail</span>}
      </div>

      {!hasCurrent ? (
        <p className="text-[13px] text-[var(--text-tertiary)]" data-testid="metric-no-data">No measurement recorded yet</p>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            {baselineProvided ? (
              <span className="text-[13px] text-[var(--text-tertiary)]" data-testid="metric-baseline">{baseline.baseline_value}</span>
            ) : (
              <span className="text-[11px] text-[var(--text-tertiary)] italic" data-testid="metric-baseline-unavailable">Baseline unavailable</span>
            )}
            {baselineProvided && <span className="text-[var(--text-tertiary)]">→</span>}
            <span className="text-[18px] font-semibold text-[var(--text-primary)]" data-testid="metric-current">{current.current_value}</span>
          </div>
          <div className="flex items-center gap-2">
            {baselineProvided && current.delta_pct !== null && current.delta_pct !== undefined ? (
              <span className={cn("text-[11px] font-mono", current.delta_pct > 0 ? "text-[var(--success)]" : current.delta_pct < 0 ? "text-[var(--error)]" : "text-[var(--text-tertiary)]")} data-testid="metric-delta">
                {formatDelta(current.delta_pct)}
              </span>
            ) : (
              <span className="text-[10px] text-[var(--text-tertiary)]" data-testid="metric-delta-unavailable">Delta unavailable</span>
            )}
            {status && <StatusBadge value={status} />}
          </div>
        </>
      )}
    </div>
  );
}
